import { ChangeEvent, KeyboardEvent, MouseEvent } from 'react'

import { TFunction } from 'react-i18next'
import throttle from 'lodash/throttle'
import randomColor from 'randomcolor'

import { Chat } from './chat'

import ChatService from 'modules/chat/service'
import { Lead } from 'modules/lead/types'
import {
  MessageWithReply,
  MessageReceivedPayload,
  MessageDeletedPayload,
  MessageOrigin,
  GetProfileResponse,
  GetConversationResponse,
  MessageReceivedContact
} from 'modules/chat/types'

export interface IProps {
  t: TFunction,
  hide?: boolean,
  liveHash: string,
  conversationId: string,
  fromMux?: boolean,
  mediaCode?: string,
  isMobile: boolean,
  isLandscape: boolean,
  profile: GetProfileResponse,
  conversation: GetConversationResponse,
  adminIds: string[],
  lead: Lead,
  isMuted: boolean
}

export interface IState {
  messages: MessageWithReply[][],
  textInput: string,
  viewers: number,
  scrollInBottom: boolean,
  loadHistory: boolean,
  reply?: MessageWithReply,
  showMessageOrigin: string,
  maxRows: number
}

export class Controller {
  view: Chat
  dataMessages: MessageWithReply[] = []
  readonly MAX_ROWS_MOBILE: number = 3
  readonly MAX_ROWS_DESKTOP: number = 5
  iOS: boolean = /iPad|iPhone|iPod/.test(navigator.platform)
  userColorMap: { [userId: string]: string }

  constructor(view: Chat) {
    this.view = view

    this.view.state = {
      messages: [],
      textInput: '',
      viewers: 0,
      scrollInBottom: true,
      loadHistory: true,
      reply: void 0,
      showMessageOrigin: '',
      maxRows: this.getMaxRows()
    }

    this.configRealtimeModule()
    this.userColorMap = {}
  }

  private configRealtimeModule = async () => {
    ChatService.subscribeEvents({
      messageReceived: this.messageReceived,
      messageDeleted: this.messageDeleted
    })
  }

  private messageParser = (messages: MessageReceivedPayload[]): MessageWithReply[] => {
    const messageList = messages.map(message => {
      if (!this.userColorMap[message.contactId]) {
        this.userColorMap[message.contactId] = randomColor({ luminosity: 'dark' })
      }

      const messageOrigin: MessageOrigin | undefined = message.metadata.parentContent && {
        contactId: message.metadata.parentContent.contact.id,
        message: message.metadata.parentContent.text,
        messageId: message.parentId || '',
        name: message.metadata.parentContent.contact.name || '',
        owner: message.metadata.parentContent.contact.id === this.view.props.profile.id,
        userId: Number(message.metadata.parentContent.contact.userId) || void 0
      }

      return {
        color: this.userColorMap[message.contactId],
        contactId: message?.contactId,
        createdAt: message.sentAt,
        message: message.content.text,
        messageId: message.id,
        name: message?.contact?.name || '',
        owner: this.showModeratorTag(message.contactId),
        photo: message?.contact?.picture || '',
        reply: !!(message.parentId),
        userId: Number(message.contact?.userId) || void 0,
        messageOrigin
      }
    }).sort((a, b) => a.createdAt - b.createdAt)

    return messageList
  }

  setupChat = async () => {
    const { conversationId, profile } = this.view.props
    const { lead } = this.view.props

    if (!conversationId) {
      return
    }

    if (profile.id) {
      this.userColorMap[profile.id] = lead.color || ''
    }

    this.getHistoryMessages()
  }

  private getHistoryMessages = async () => {
    const { conversationId } = this.view.props
    const newMessages = await ChatService.getMessages({ conversationId })

    this.dataMessages = this.messageParser(newMessages) || []

    this.setMessages(this.dataMessages)
  }

  private setMessages = (messages: MessageWithReply[], isMe?: boolean) => {
    const fullScreenEnable = Boolean(document?.fullscreenElement)

    const scrollInBottom = (
      this.view.state.scrollInBottom
      || this.view.state.loadHistory
      || isMe
      || fullScreenEnable
    )

    const size = 10
    const messagesBatch = []

    for (let i = 0; i < messages.length; i += size) {
      messagesBatch.push(messages.slice(i, i + size))
    }

    this.view.setState({ messages: messagesBatch, scrollInBottom, loadHistory: false }, () => {
      scrollInBottom && this.scrollToBottom()
    })
  }

  messageDeleted = (event: MessageDeletedPayload) => {
    for (let i = 0; i <= this.dataMessages.length - 1; i += 1) {
      if (this.dataMessages[i].messageId === event.id) {
        this.dataMessages.splice(i, 1)
      }
    }

    this.setMessages(this.dataMessages)
  }

  private verifyPositionScroll = () => {
    if (this.view.chatBody.current) {
      const {
        scrollTop,
        clientHeight,
        scrollHeight
      } = this.view.chatBody.current

      const isPositionBottom = Math.ceil(scrollTop) + clientHeight === scrollHeight

      if (isPositionBottom !== this.view.state.scrollInBottom) {
        this.view.setState({ scrollInBottom: isPositionBottom })
      }
    }
  }

  throttledVerifyPositionScroll = throttle(
    this.verifyPositionScroll,
    300
  )

  scrollToBottom = () => {
    if (this.view.chatBody.current) {
      const { scrollHeight, clientHeight } = this.view.chatBody.current

      if (!this.view.state.scrollInBottom) {
        this.view.setState({ scrollInBottom: true })
      }
      this.view.chatBody.current.scrollTop = scrollHeight - clientHeight
    }
  }

  onChangeInput = (event: ChangeEvent<HTMLTextAreaElement>) => {
    this.view.setState({
      textInput: event.target.value
    })
  }

  private hasMessage = (messageId: string) => {
    return this.dataMessages.some(message => message.messageId === messageId)
  }

  messageReceived = (payload: MessageReceivedPayload) => {
    const messageData = this.messageParser([payload])[0]

    const isMine = payload?.contactId === this.view.props.profile.id

    if (isMine && this.hasMessage(messageData.messageId)) {
      return
    }

    this.dataMessages.push(messageData)
    this.setMessages(this.dataMessages)
  }

  onKeyPress = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.key === 'Enter' && event.shiftKey) || (event.key === 'Enter' && this.iOS)) {
      this.view.setState({ textInput: this.view.state.textInput.replace(/\n/g, '\n') })
    } else if (event.key === 'Enter' && !this.iOS) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  clearMessageInput = () => {
    this.view.setState({ textInput: '', reply: void 0, scrollInBottom: true }, this.scrollToBottom)
  }

  sendMessage = async () => {
    const { profile, isMuted, lead } = this.view.props

    if (isMuted) {
      this.clearMessageInput()

      return
    }

    const { textInput, reply } = this.view.state
    const textMessage = textInput.trim()

    if (textMessage.length !== 0) {
      this.clearMessageInput()

      const message = await ChatService.sendTextMessage({
        conversationId: this.view.props.conversationId,
        message: textMessage,
        ...(reply && { reply })
      })

      if (message) {
        const contact: MessageReceivedContact = {
          ...profile,
          picture: lead.photo || profile.picture || ''
        }

        const parsedMessage = this.messageParser([{ ...message, contact }])[0]

        this.dataMessages.push(parsedMessage)
        this.setMessages(this.dataMessages, true)
      }
    }
  }

  addEmoji = (emoji: string) => {
    this.view.setState({
      textInput: `${this.view.state.textInput}${emoji}`
    })
  }

  replyMessage = (message: MessageWithReply) => {
    const input = document.getElementById('input-message')

    if (input) {
      input.focus()
    }
    this.view.setState({ reply: message })
  }

  removeReplyMessage = () => {
    this.view.setState({ reply: void 0 })
  }

  private scrollToMessage = (message: string) => {
    let ticking = false
    const messageSelect = document.getElementById(message)

    if (!ticking) {
      window.requestAnimationFrame(() => {
        if (messageSelect) {
          messageSelect.scrollIntoView()
        }
        ticking = false
      })

      ticking = true
    }
  }

  onClickMessage = (event: MouseEvent, message: string) => {
    this.view.setState({ showMessageOrigin: message })
    event.preventDefault()
    this.scrollToMessage(message)

    setTimeout(() => {
      this.view.setState({ showMessageOrigin: '' })
    }, 1000)
  }

  resizeListener = () => {
    this.view.setState({
      maxRows: this.getMaxRows()
    })
  }

  private getMaxRows = (): number => {
    const { isMobile, isLandscape } = this.view.props

    const numberRows = isMobile || isLandscape ? this.MAX_ROWS_MOBILE : this.MAX_ROWS_DESKTOP

    return numberRows
  }

  removeChatEventListner = () => {
    ChatService.removeEventSubscription({
      messageReceived: this.messageReceived,
      messageDeleted: this.messageDeleted
    })
  }

  showModeratorTag = (contactId: string) => {
    const { conversation, adminIds } = this.view.props

    return contactId === conversation.metadata.owner || adminIds.includes(contactId)
  }
}
